import { Component, ElementRef, HostListener, OnInit, inject } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { SettingsService } from '../services/settings.service';
import { AuthService } from '../services/auth.service';

@Component({
  selector: 'app-shell',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, RouterOutlet],
  template: `
    <div class="min-h-screen flex flex-col print:block print:bg-white">
      <header class="bg-surface/90 backdrop-blur border-b border-line print:hidden sticky top-0 z-20">
        <div class="app-rail"></div>
        <div class="h-16 flex items-center justify-between px-6">
          <div class="flex items-center gap-2.5">
            <div class="w-8 h-8 app-logo-mark">
              <svg viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" class="w-4.5 h-4.5">
                <polyline points="6,9 6,3 18,3 18,9" />
                <rect x="4" y="9" width="16" height="8" rx="1.5" />
                <rect x="6" y="15" width="12" height="6" rx="1" />
              </svg>
            </div>
            <span class="font-semibold text-ink text-sm">{{ firmName || 'Retail Printing' }}</span>
          </div>

          <div class="flex items-center gap-5">
            <span class="text-sm text-muted">Hi, <span class="font-medium text-ink">{{ userName }}</span></span>
            <button (click)="signOut()" class="app-btn-secondary !py-1.5 !px-3 text-xs">Sign Out</button>
          </div>
        </div>

        <nav class="h-11 flex items-center gap-1 px-6 border-t border-line-soft relative">
          <a routerLink="/dashboard" routerLinkActive="nav-top-item-active" class="nav-top-item">Dashboard</a>

          <div class="relative">
            <button
              type="button"
              (click)="toggleMenu('settings', $event)"
              class="nav-top-item"
              [class.nav-top-item-active]="openMenu === 'settings'"
            >
              Settings / Setup
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-3.5 h-3.5">
                <polyline points="6,9 12,15 18,9" />
              </svg>
            </button>
            @if (openMenu === 'settings') {
              <div class="nav-dropdown">
                <a routerLink="/settings/categories" routerLinkActive="nav-dropdown-item-active" class="nav-dropdown-item" (click)="closeMenu()">
                  Manage Product Categories
                </a>
                <a routerLink="/settings/products" routerLinkActive="nav-dropdown-item-active" class="nav-dropdown-item" (click)="closeMenu()">
                  Manage Products
                </a>
                <a routerLink="/settings/invoice" routerLinkActive="nav-dropdown-item-active" class="nav-dropdown-item" (click)="closeMenu()">
                  Invoice Settings
                </a>
              </div>
            }
          </div>

          <a routerLink="/orders/add" routerLinkActive="nav-top-item-active" class="nav-top-item">Add Order</a>
          <a routerLink="/orders/pending" routerLinkActive="nav-top-item-active" class="nav-top-item">Pending Orders</a>
          <a routerLink="/orders/history" routerLinkActive="nav-top-item-active" class="nav-top-item">Orders History</a>
          <a routerLink="/orders/quotations" routerLinkActive="nav-top-item-active" class="nav-top-item">Quotations</a>
          <a routerLink="/payments/advance" routerLinkActive="nav-top-item-active" class="nav-top-item">Advance Payment</a>

          <div class="relative">
            <button
              type="button"
              (click)="toggleMenu('reports', $event)"
              class="nav-top-item"
              [class.nav-top-item-active]="openMenu === 'reports'"
            >
              Reports
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-3.5 h-3.5">
                <polyline points="6,9 12,15 18,9" />
              </svg>
            </button>
            @if (openMenu === 'reports') {
              <div class="nav-dropdown">
                <a routerLink="/reports/sales" routerLinkActive="nav-dropdown-item-active" class="nav-dropdown-item" (click)="closeMenu()">
                  Sales Report
                </a>
                <a routerLink="/reports/account-summary" routerLinkActive="nav-dropdown-item-active" class="nav-dropdown-item" (click)="closeMenu()">
                  Accounts Summary
                </a>
                <a routerLink="/reports/account-statement" routerLinkActive="nav-dropdown-item-active" class="nav-dropdown-item" (click)="closeMenu()">
                  Customer Statement
                </a>
                <a routerLink="/reports/cash-collection" routerLinkActive="nav-dropdown-item-active" class="nav-dropdown-item" (click)="closeMenu()">
                  Cash Collection Report
                </a>
                <a routerLink="/reports/dues" routerLinkActive="nav-dropdown-item-active" class="nav-dropdown-item" (click)="closeMenu()">
                  Outstanding Dues
                </a>
              </div>
            }
          </div>
        </nav>
      </header>

      <main class="flex-1 p-6 print:p-0">
        <router-outlet />
      </main>
    </div>
  `,
  styles: [
    `
      .nav-top-item {
        position: relative;
        display: inline-flex;
        align-items: center;
        gap: 0.3rem;
        padding: 0.5rem 0.75rem;
        border-radius: 0.5rem;
        font-size: 0.875rem;
        font-weight: 600;
        color: var(--color-muted);
        transition: color 0.15s, background 0.15s;
      }
      .nav-top-item:hover {
        background: rgba(255, 255, 255, 0.05);
        color: var(--color-ink);
      }
      .nav-top-item-active {
        color: #a5b4fc;
      }
      .nav-top-item-active::after {
        content: "";
        position: absolute;
        left: 0.75rem;
        right: 0.75rem;
        bottom: -0.0625rem;
        height: 2px;
        border-radius: 9999px;
        background-image: linear-gradient(90deg, #6366f1, #38bdf8);
      }
      .nav-dropdown {
        position: absolute;
        top: calc(100% + 0.375rem);
        left: 0;
        min-width: 15rem;
        background: var(--color-surface-2);
        border: 1px solid var(--color-line);
        border-radius: 0.75rem;
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.45), 0 8px 10px -6px rgba(0, 0, 0, 0.35);
        padding: 0.375rem;
        z-index: 30;
      }
      .nav-dropdown-item {
        display: block;
        padding: 0.5rem 0.75rem;
        border-radius: 0.5rem;
        font-size: 0.875rem;
        font-weight: 500;
        color: var(--color-muted);
        transition: color 0.15s, background 0.15s;
      }
      .nav-dropdown-item:hover {
        background: linear-gradient(135deg, rgba(99, 102, 241, 0.14), rgba(56, 189, 248, 0.1));
        color: var(--color-ink);
      }
      .nav-dropdown-item-active {
        color: #a5b4fc;
        background: linear-gradient(135deg, rgba(99, 102, 241, 0.16), rgba(56, 189, 248, 0.12));
      }
    `,
  ],
})
export class ShellComponent implements OnInit {
  private settingsService = inject(SettingsService);
  private auth = inject(AuthService);
  private router = inject(Router);
  private elementRef = inject(ElementRef);

  firmName = '';
  openMenu: 'settings' | 'reports' | null = null;

  // The signed-in staff member's display name, for the header greeting.
  get userName(): string {
    return this.auth.user()?.name || 'there';
  }

  ngOnInit(): void {
    this.settingsService.getInvoiceSettings().subscribe({
      next: (s) => (this.firmName = s.firm_name),
      error: () => {
        // Cosmetic only — the firm name is just the header label, and the rest of the
        // app works without it, so fall back rather than blocking the shell.
        this.firmName = 'Shekhawat Printers';
      },
    });
  }

  toggleMenu(menu: 'settings' | 'reports', event: Event): void {
    event.stopPropagation();
    this.openMenu = this.openMenu === menu ? null : menu;
  }

  closeMenu(): void {
    this.openMenu = null;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.openMenu = null;
    }
  }

  signOut(): void {
    this.auth.logout();
    this.router.navigateByUrl('/login');
  }
}
